import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// Toast Manager - Singleton pattern
class ToastManager {
  constructor() {
    this.toasts = [];
    this.listeners = [];
    this.nextId = 1;
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  show(options) {
    const {
      type = 'info',
      title = '',
      message = '',
      duration = 4000,
      closable = true
    } = options;

    const id = this.nextId++;
    const toast = { id, type, title, message, duration, closable, show: true };
    
    this.toasts.push(toast);
    this.notify();

    console.log('🍞 Toast added:', toast);
    console.log('🍞 Total toasts:', this.toasts.length);

    // Auto remove
    if (duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }

    return id;
  }

  remove(id) {
    const toast = this.toasts.find(t => t.id === id);
    if (toast) {
      toast.show = false;
      this.notify();
      
      setTimeout(() => {
        this.toasts = this.toasts.filter(t => t.id !== id);
        this.notify();
      }, 300);
    }
  }

  success(message, title = 'Thành công') {
    console.log('✅ Toast.success called:', message);
    return this.show({ type: 'success', title, message });
  }

  error(message, title = 'Lỗi') {
    console.log('❌ Toast.error called:', message);
    return this.show({ type: 'error', title, message, duration: 5000 });
  }

  warning(message, title = 'Cảnh báo') {
    console.log('⚠️ Toast.warning called:', message);
    return this.show({ type: 'warning', title, message });
  }

  info(message, title = 'Thông tin') {
    console.log('ℹ️ Toast.info called:', message);
    return this.show({ type: 'info', title, message });
  }
}

// Create singleton instance
const toastManager = new ToastManager();

// Toast Component
function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    console.log('🍞 ToastContainer mounted');
    const unsubscribe = toastManager.subscribe((newToasts) => {
      console.log('🍞 ToastContainer received update:', newToasts);
      setToasts(newToasts);
    });
    return () => {
      console.log('🍞 ToastContainer unmounted');
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    console.log('🍞 ToastContainer toasts state updated:', toasts);
  }, [toasts]);

  const icons = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    info: 'info'
  };

  const colors = {
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6'
  };

  console.log('🍞 ToastContainer rendering with', toasts.length, 'toasts');

  return createPortal(
    <div 
      className="fixed top-4 right-4 sm:top-5 sm:right-5 flex flex-col gap-3 pointer-events-none max-w-[calc(100vw-2rem)] sm:max-w-md"
      style={{ 
        position: 'fixed', 
        top: '20px', 
        right: '20px', 
        zIndex: 99999,
        pointerEvents: 'none'
      }}
    >
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`min-w-[280px] sm:min-w-[300px] max-w-full p-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex items-start gap-3 pointer-events-auto transform transition-all duration-300 ${
            toast.show ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
          }`}
          style={{
            borderLeft: `4px solid ${colors[toast.type]}`,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div className="flex-shrink-0 mt-0.5">
            <span 
              className="material-symbols-rounded text-xl sm:text-2xl"
              style={{ color: colors[toast.type] }}
            >
              {icons[toast.type]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            {toast.title && (
              <div className="font-semibold text-sm mb-0.5 text-gray-900 dark:text-gray-100">
                {toast.title}
              </div>
            )}
            <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed break-words">
              {toast.message}
            </div>
          </div>
          {toast.closable && (
            <button
              onClick={() => toastManager.remove(toast.id)}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors mt-0.5"
            >
              <span className="material-symbols-rounded text-base text-gray-500 dark:text-gray-400">close</span>
            </button>
          )}
        </div>
      ))}
    </div>,
    document.body
  );
}

// Export both the component and the manager
export { ToastContainer, toastManager as Toast };
export default ToastContainer;
